import {
	Button,
	Combobox,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Info,
	NativeSelect,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { MoreHorizontal } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Async, PageHead, useAction } from "@/components/ui";
import { useT } from "@/i18n";
import { type AvailableModels, api, type ModelCategory } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

export function ModelsPage() {
	const t = useT();
	const state = useAsync(() => api.availableModels(), []);
	return (
		<>
			<PageHead title={t("models.title")} help={t("models.lede")} />
			<Async state={state}>
				{(data) => (
					<>
						{data.roles?.length ? (
							<DefaultsForm data={data} onSaved={state.reload} />
						) : (
							<div className="banner" role="status">
								{t("models.defaultsUnavailable")}
								<Button variant="default" onClick={state.reload}>
									{t("state.retry")}
								</Button>
							</div>
						)}
						<section className="section">
							<h2>
								{t("models.available")} <Info>{t("models.hint")}</Info>
							</h2>
							<ModelsForm data={data} onSaved={state.reload} />
						</section>
					</>
				)}
			</Async>
		</>
	);
}

/** One model per job; the panel opens every picker for that job on it. */
function DefaultsForm({ data, onSaved }: { data: AvailableModels; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [defaults, setDefaults] = useState(() => Object.fromEntries(data.roles.map((r) => [r.role, r.model])));

	async function submit(event: FormEvent) {
		event.preventDefault();
		const saved = await run(
			() => api.putDefaultModels(defaults),
			() => t("state.saved"),
		);
		if (saved) onSaved();
	}

	return (
		<form className="form" onSubmit={submit}>
			<fieldset disabled={busy}>
				<h2>
					{t("models.defaults")} <Info>{t("models.defaultsHint")}</Info>
				</h2>
				<div className="table-scroll">
					<Table className="text-[length:var(--t-sm)]">
						<TableHeader>
							<TableRow>
								<TableHead>{t("models.role")}</TableHead>
								<TableHead>{t("models.model")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.roles.map(({ role, category }) => {
								// A curated category offers only its list, so a default outside
								// it would be a default nobody is offered.
								const curated = data.selected[category] ?? [];
								const options = data.models
									.filter((m) => m.category === category && (!curated.length || curated.includes(m.id)))
									.map((m) => ({ value: m.id, label: m.name || m.id }));
								return (
									<TableRow key={role}>
										<TableCell>
											{t(`models.role.${role}`)}
											<div className="hint small">{t(`models.role.${role}.use`)}</div>
										</TableCell>
										<TableCell>
											<div className="model-picker">
												<Combobox
													searchable
													value={defaults[role] ?? ""}
													options={options}
													onChange={(id) => setDefaults((prev) => ({ ...prev, [role]: id }))}
													searchPlaceholder={t("models.search")}
													emptyMessage={t("models.noMatch")}
													aria-label={t(`models.role.${role}`)}
												/>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
				<div className="row">
					<span className="spacer" />
					<Button variant="default" type="submit">
						{t("state.save")}
					</Button>
				</div>
			</fieldset>
		</form>
	);
}

function ModelsForm({ data, onSaved }: { data: AvailableModels; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [category, setCategory] = useState<ModelCategory>(data.categories[0] ?? "text");
	const [selected, setSelected] = useState<Partial<Record<ModelCategory, string[]>>>(data.selected);

	const catalogue = data.models.filter((m) => m.category === category);
	const ids = selected[category] ?? [];
	const byId = new Map(catalogue.map((m) => [m.id, m]));

	const set = (next: string[]) => setSelected((prev) => ({ ...prev, [category]: next }));
	const move = (at: number, by: number) => {
		const next = [...ids];
		const [row] = next.splice(at, 1);
		next.splice(at + by, 0, row);
		set(next);
	};

	async function submit(event: FormEvent) {
		event.preventDefault();
		const saved = await run(
			() => api.putAvailableModels(selected),
			() => t("state.saved"),
		);
		if (saved) onSaved();
	}

	return (
		<form className="form" onSubmit={submit}>
			<fieldset disabled={busy}>
				<div className="row">
					<NativeSelect
						aria-label={t("models.category")}
						value={category}
						onChange={(e) => setCategory(e.target.value as ModelCategory)}
					>
						{data.categories.map((name) => (
							<option key={name} value={name}>
								{t(`models.category.${name}`)}
							</option>
						))}
					</NativeSelect>
					<span className="hint">{ids.length ? t("models.count", { n: ids.length }) : t("models.all")}</span>
					<span className="spacer" />
					<div className="model-picker">
						<Combobox
							searchable
							value=""
							options={catalogue
								.filter((m) => !ids.includes(m.id))
								.map((m) => ({ value: m.id, label: m.name || m.id }))}
							onChange={(id) => set([...ids, id])}
							placeholder={t("models.add")}
							searchPlaceholder={t("models.search")}
							emptyMessage={t("models.noMatch")}
							aria-label={t("models.add")}
						/>
					</div>
				</div>
				{ids.length > 0 && (
					<div className="table-scroll">
						<Table className="text-[length:var(--t-sm)]">
							<TableHeader>
								<TableRow>
									<TableHead>{t("models.order")}</TableHead>
									<TableHead>{t("models.model")}</TableHead>
									<TableHead>{t("models.provider")}</TableHead>
									<TableHead className="num text-right whitespace-nowrap">{t("models.online")}</TableHead>
									<TableHead className="text-right">{t("state.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{ids.map((id, at) => {
									const row = byId.get(id);
									return (
										<TableRow key={id}>
											<TableCell className="num text-right whitespace-nowrap">{at + 1}</TableCell>
											<TableCell>
												{row?.name || id} <span className="mono faint">{id}</span>
												{/* A curated id the board no longer sells is offered to nobody; it stays
												    listed here so an operator can see why the picker is one shorter. */}
												{!row && <span className="hint small">{t("models.gone")}</span>}
											</TableCell>
											<TableCell>{row?.provider ?? "—"}</TableCell>
											<TableCell className="num text-right whitespace-nowrap">{row?.online ?? 0}</TableCell>
											<TableCell>
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															type="button"
															size="sm"
															variant="ghost"
															aria-label={`${t("state.actions")}: ${row?.name || id}`}
														>
															<MoreHorizontal />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem disabled={busy || at === 0} onSelect={() => move(at, -1)}>
															{t("models.up")}
														</DropdownMenuItem>
														<DropdownMenuItem disabled={busy || at === ids.length - 1} onSelect={() => move(at, 1)}>
															{t("models.down")}
														</DropdownMenuItem>
														<DropdownMenuSeparator />
														<DropdownMenuItem
															disabled={busy}
															className="text-danger"
															onSelect={() => set(ids.filter((other) => other !== id))}
														>
															{t("models.remove")}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</div>
				)}
				<div className="row">
					<span className="spacer" />
					<Button variant="default" type="submit">
						{t("state.save")}
					</Button>
				</div>
			</fieldset>
		</form>
	);
}
